
import * as React from 'react';
import { IItemCacheConfiguration, createItemCacheStore, IItemCacheStore } from '../../store/createStore';
import {
  BackgroundTaskContextProvider
} from '../backgroundTaskRunner/BackgroundTaskHost';
import { backgroundTaskQueueFacet } from '../../facets/ConfigurationFacets';
import { IBackgroundTaskQueue, IBackgroundTask } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { readConfiguration } from '../../operations/configuration/readConfiguration';
import { IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { ConnectToItemCacheProvider } from '../decorators/connectToItemCache';
import { IReactChildrenProps } from '../../utilities/components/Props';

/**
 * Represents props passed to `ItemCacheProvider`.
 * @public
 */
export interface IItemCacheProviderProps {
  /**
   * Configuration to pass to a new `ItemCacheStore`, if `store` is not already passed via props
   * and a store is not available via React context.
   */
  configuration?: IItemCacheConfiguration;
  /**
   * A store to provide to downstream components.
   * If not specified, `ItemCacheProvider` will consume an existing store from React context.
   * If one is not available,
   */
  store?: IItemCacheStore;
  /**
   * A creator for an action to be dispatched when the provider mounts.
   * If there is a store available in context, the `ItemCacheProvider` will wait until the provider
   * has already mounted before dispatching the again. Otherwise, it will dispatch before initial render
   * to prevent a cascading update.
   */
  initializer?: () => IItemCacheThunkAction<void>;
}

/**
 * Context used only within `ItemCacheProvider` to share stores when possible within the React tree.
 * This is not intended for use by downstream components to obtain data from the store.
 */
const ITEM_CACHE_STORE_CONTEXT = React.createContext<IItemCacheStore | undefined>(undefined);

/**
 * Component which provides a context containing an item cache.
 * The item cache can be consumed in child components by using the `connectToItemCache` function.
 * @public
 */
export const ItemCacheProvider: React.ComponentType<IItemCacheProviderProps> = (
  props: IItemCacheProviderProps & IReactChildrenProps
): JSX.Element => {
  const {
    store: propsStore,
    configuration,
    initializer,
    children
  } = props;

  if ('store' in props && !props.store) {
    // Protect host apps against accidentally initializing a brand new store when attempting to pass
    // an existing store instance.
    throw new Error(`The 'store' prop was passed to ItemCacheProvider, but did not have a value.`);
  }

  if (propsStore && configuration) {
    throw new Error(`Cannot initialize an ItemCacheProvider with both 'store' and 'configuration' props.`);
  }

  const contextStore = React.useContext(ITEM_CACHE_STORE_CONTEXT);

  if (contextStore && configuration) {
    throw new Error(`Cannot initialize an ItemCacheProvider with 'configuration' when a store is already available via context.`);
  }

  const ownStoreRef = React.useRef<IItemCacheStore>();
  const headlessBackgroundTaskQueueRef = React.useRef<IBackgroundTaskQueue>();

  let ownStore: {
    dispose(): void;
  } | undefined;

  const store = propsStore || contextStore || (ownStore = ownStoreRef.current || (ownStoreRef.current = createItemCacheStore({
    configuration
  })));

  if (store !== contextStore) {
    // The store is not the one from context, execute the initializer immediately so any data can be rendered synchronously.
    if (initializer) {
      store.dispatch(initializer());
    }
  }

  React.useLayoutEffect(() => {
    if (store === contextStore) {
      // If the store is the one from context, execute the initializer on mount to avoid disrupting the data flow.
      if (initializer) {
        store.dispatch(initializer());
      }
    }
  }, []);

  React.useLayoutEffect(() => {
    // On unmount, dispose the 'local' store if it was ever created.
    return () => {
      if (ownStore) {
        ownStore.dispose();
      }
    };
  }, []);

  if (store !== contextStore) {
    // The the store was not absorbed from context, then the headless background task host needs to be wrapped around
    // everything, if provided.
    const headlessBackgroundTaskQueue = headlessBackgroundTaskQueueRef.current || (
      headlessBackgroundTaskQueueRef.current = getHeadlessBackgroundTaskQueue(store)
    );

    return (
      // If the store does not match the one currently available in React context,
      // then this provider needs to provide the hosts for background tasks, as well as the Redux store.
      // The headless queue will either be supplied in-line or routed to a queue from configuration.
      <ITEM_CACHE_STORE_CONTEXT.Provider value={ store }>
        <ConnectToItemCacheProvider store={ store }>
          <BackgroundTaskContextProvider
            scope='headless'
            queue={ headlessBackgroundTaskQueue }
            onQueueTask={ headlessBackgroundTaskQueue ? undefined : wrapWithGlobalTaskProvider }
          >
            <BackgroundTaskContextProvider scope='global'>
              { children }
            </BackgroundTaskContextProvider>
          </BackgroundTaskContextProvider>
        </ConnectToItemCacheProvider>
      </ITEM_CACHE_STORE_CONTEXT.Provider>
    );
  }

  return (
    <>{ children }</>
  );
};

function getHeadlessBackgroundTaskQueue(store: IItemCacheStore): IBackgroundTaskQueue | undefined {
  const {
    backgroundTaskQueueFromConfiguration
  } = store.dispatch(readConfiguration({
    backgroundTaskQueueFromConfiguration: backgroundTaskQueueFacet
  }));

  return backgroundTaskQueueFromConfiguration ? {
    enqueue: (task: IBackgroundTask) => {
      backgroundTaskQueueFromConfiguration.enqueue({
        ...task,
        content: (
          <ItemCacheProvider store={ store }>
            { task.content }
          </ItemCacheProvider>
        )
      });
    }
  } : undefined;
}

function wrapWithGlobalTaskProvider(task: IBackgroundTask): IBackgroundTask {
  return {
    ...task,
    content: (
      // Headless tasks provided by this provider need to have the `global` scope added back.
      <BackgroundTaskContextProvider scope='global'>
        { task.content }
      </BackgroundTaskContextProvider>
    )
  };
}
